(() => {
  'use strict';
  const config = window.craftmarkConfig;
  if (!config) return;
  const managed = new Set(config.managedProducts.map(String));
  const bypass = new WeakSet();
  let refreshing = false;
  let refreshAgain = false;
  let timer;

  async function validate(lines) {
    const response = await fetch(config.endpoint, {
      method: 'POST', headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ lines }), credentials: 'omit', cache: 'no-store',
      signal: AbortSignal.timeout(20000),
    });
    if (!response.ok) throw new Error(config.messages.unavailable);
    const result = await response.json();
    if (typeof result.valid !== 'boolean' || !Array.isArray(result.errors)) throw new Error(config.messages.unavailable);
    return result;
  }

  function addLine(data) {
    const properties = {};
    for (const [key, value] of data.entries()) {
      const match = key.match(/^properties\[(.+)\]$/);
      if (match && typeof value === 'string') properties[match[1]] = value;
    }
    return { variantId: String(data.get('id') || ''), quantity: Number(data.get('quantity') || 1), properties };
  }

  async function validateAdd(data) {
    const result = await validate([addLine(data)]);
    if (!result.valid) throw new Error(result.errors[0]?.error || config.messages.missing);
  }

  const cartUrl = () => `${window.Shopify?.routes?.root || '/'}cart.js`;
  async function readCart() {
    const response = await fetch(cartUrl(), { cache: 'no-store', signal: AbortSignal.timeout(15000) });
    if (!response.ok) throw new Error(config.messages.unavailable);
    const cart = await response.json();
    if (!Array.isArray(cart.items)) throw new Error(config.messages.unavailable);
    return cart;
  }
  const relevant = cart => cart.items.filter(item => managed.has(String(item.product_id)));
  const fingerprint = cart => JSON.stringify(relevant(cart).map(item => [item.key, item.variant_id, item.quantity, item.properties]));

  function controls(disabled) {
    document.querySelectorAll('[data-craftmark-checkout]').forEach(button => {
      button.disabled = disabled;
      button.setAttribute('aria-disabled', String(disabled));
    });
  }

  function displayErrors(errors, items) {
    document.querySelectorAll('[data-craftmark-errors]').forEach(container => {
      const signature = JSON.stringify([errors, items.map(item => [item.title, item.url])]);
      if (container.dataset.signature === signature) return;
      container.dataset.signature = signature;
      container.replaceChildren();
      for (const error of errors) {
        const paragraph = document.createElement('p');
        paragraph.textContent = error.error;
        const item = items[error.index];
        if (item) {
          const link = document.createElement('a');
          // Use the product page so current variant options determine the designer URL.
          link.href = item.url;
          link.textContent = `${config.messages.customize}: ${item.product_title || item.title}`;
          paragraph.append(' ', link);
        }
        container.append(paragraph);
      }
    });
  }

  async function verifyCart() {
    controls(true);
    const cart = await readCart();
    const items = relevant(cart);
    const result = items.length ? await validate(items.map(item => ({
      variantId: String(item.variant_id), quantity: item.quantity, properties: item.properties || {},
    }))) : { valid: true, errors: [] };
    const current = await readCart();
    if (fingerprint(cart) !== fingerprint(current)) throw new Error(config.messages.changed);
    displayErrors(result.errors, items);
    document.getElementById('main-cart-footer')?.classList.toggle('is-empty', current.items.length === 0);
    controls(!result.valid || current.items.length === 0);
    if (!result.valid) throw new Error(result.errors[0]?.error || config.messages.missing);
  }

  async function refresh() {
    if (!document.querySelector('[data-craftmark-checkout]')) return;
    if (refreshing) { refreshAgain = true; return; }
    refreshing = true;
    try { await verifyCart(); }
    catch (error) {
      controls(true);
      // Preserve detailed per-line errors already rendered by validation.
      if (!document.querySelector('[data-craftmark-errors] p')) displayErrors([{ error: error.message }], []);
    } finally {
      refreshing = false;
      if (refreshAgain) { refreshAgain = false; scheduleRefresh(); }
    }
  }
  function scheduleRefresh() {
    clearTimeout(timer);
    timer = setTimeout(refresh, 100);
  }

  async function checkout(action) {
    try {
      if (window.CraftMarkBeforeCheckout) await window.CraftMarkBeforeCheckout();
      await verifyCart(); action();
    }
    catch (error) {
      controls(true);
      if (!document.querySelector('[data-craftmark-errors] p')) displayErrors([{ error: error.message }], []);
    }
  }

  document.addEventListener('submit', event => {
    const form = event.target;
    if (bypass.delete(form)) return;
    const target = new URL(form.action, location.href);
    const submitter = event.submitter;
    if (submitter?.name === 'checkout' || /\/checkout\/?$/.test(target.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(() => {
        const liveForm = form.id ? document.getElementById(form.id) : form;
        const liveButton = submitter?.id ? document.getElementById(submitter.id) : submitter;
        if (!liveForm) { location.assign(`${window.Shopify?.routes?.root || '/'}checkout`); return; }
        bypass.add(liveForm); liveForm.requestSubmit(liveButton || undefined);
      });
    } else if (/\/cart\/add(?:\.js)?\/?$/.test(target.pathname) && !form.closest('product-form')) {
      // Native forms outside Dawn's product-form, including supported reorder forms.
      event.preventDefault(); event.stopImmediatePropagation();
      const data = new FormData(form);
      validateAdd(data).then(() => {
        if (JSON.stringify(addLine(data)) !== JSON.stringify(addLine(new FormData(form)))) throw new Error(config.messages.changed);
        bypass.add(form); form.requestSubmit(submitter || undefined);
      }).catch(error => window.alert(error.message));
    }
  }, true);

  document.addEventListener('click', event => {
    const link = event.target.closest('a[href]');
    if (!link) return;
    const url = new URL(link.href, location.href);
    if (url.origin !== location.origin) return;
    if (/\/checkout\/?$/.test(url.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation();
      checkout(() => location.assign(url.href));
    } else if (/\/cart\/add\/?$/.test(url.pathname)) {
      event.preventDefault(); event.stopImmediatePropagation();
      validateAdd(url.searchParams).then(() => location.assign(url.href)).catch(error => window.alert(error.message));
    }
  }, true);

  window.CraftMarkGuard = { validateAdd, verifyCart };
  document.addEventListener('DOMContentLoaded', () => {
    // Price/upsell text changes do not change the cart. Refresh when cart
    // controls or lines are replaced; every checkout still revalidates.
    const cartStructure = '[data-craftmark-checkout], [data-craftmark-errors], [id^="CartItem-"], [id^="CartDrawer-Item-"], input[name="updates[]"]';
    const containsCartStructure = node => node.nodeType === Node.ELEMENT_NODE &&
      (node.matches(cartStructure) || node.querySelector(cartStructure));
    const observer = new MutationObserver(records => {
      if (records.some(record => [...record.addedNodes, ...record.removedNodes].some(containsCartStructure))) scheduleRefresh();
    });
    observer.observe(document.body, { childList: true, subtree: true });
    if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.cartUpdate) {
      subscribe(PUB_SUB_EVENTS.cartUpdate, scheduleRefresh);
    }
    scheduleRefresh();
  });
  window.addEventListener('pageshow', scheduleRefresh);
})();
